import {
  getToolOverride,
  getToolValidationAlwaysAllowLabel,
  getToolValidationTitle,
} from "@app/components/actions/blocked/toolValidationLabels";
import { ConversationCard } from "@app/components/assistant/conversation/cards/ConversationCard";
import { ToolValidationDetails } from "@app/components/assistant/conversation/ToolValidationDetails";
import { getIcon } from "@app/components/resources/resources_icons";
import type { MCPValidationOutputType } from "@app/lib/actions/constants";
import type { BlockedToolExecution } from "@app/lib/actions/mcp";
import { canCurrentUserRespondToParentUserMessage } from "@app/lib/api/assistant/conversation/can_current_user_respond";
import { asDisplayName } from "@app/types/shared/utils/string_utils";
import type { LightWorkspaceType, UserType } from "@app/types/user";
import { Button, Check, Checkbox, Label, XClose } from "@dust-tt/sparkle";
import { useMemo, useState } from "react";

// Display data needed to render a tool validation card, for both agent-loop and sandbox-function
// blocked tool executions.
type ToolValidationRequest = Pick<
  BlockedToolExecution,
  | "actionId"
  | "userId"
  | "approverUserId"
  | "stake"
  | "effect"
  | "inputs"
  | "metadata"
  | "approvalArgsLabel"
  | "argumentsRequiringApproval"
> & { inTaskConversation?: boolean };

interface ToolValidationCardProps {
  validationRequest: ToolValidationRequest;
  triggeringUser: UserType | null;
  // The viewer looking at the card. Passed in rather than read from `AuthContext` because shared
  // frames render this card outside of any AuthProvider.
  currentUser: UserType;
  owner: LightWorkspaceType;
  conversationId?: string | null;
  errorMessage: string | null;
  isValidating: boolean;
  isPulsing?: boolean;
  // Submits the user's decision; returns whether the submission succeeded.
  onValidate: (approved: MCPValidationOutputType) => Promise<boolean>;
}

export function ToolValidationCard({
  validationRequest,
  triggeringUser,
  currentUser,
  owner,
  conversationId,
  errorMessage,
  isValidating,
  isPulsing = false,
  onValidate,
}: ToolValidationCardProps) {
  const [neverAskAgain, setNeverAskAgain] = useState(false);

  const canCurrentUserRespond = useMemo(
    () =>
      canCurrentUserRespondToParentUserMessage({
        parentUserId: validationRequest.userId,
        currentUserId: currentUser.sId,
        approverUserId: validationRequest.approverUserId,
      }),
    [
      validationRequest.userId,
      validationRequest.approverUserId,
      currentUser.sId,
    ]
  );

  const icon = validationRequest.metadata.icon
    ? getIcon(validationRequest.metadata.icon)
    : undefined;

  const handleValidation = async (approved: "approved" | "rejected") => {
    const success = await onValidate(
      approved === "approved" && neverAskAgain ? "always_approved" : approved
    );
    if (success) {
      setNeverAskAgain(false);
    }
  };

  // An outward action is authorized for this exact action and nothing more, so
  // the checkbox below records how the user feels about it rather than granting
  // anything: standing grants are deferred, and the answer is what a later
  // decision on them will rest on.
  const isOutward = validationRequest.effect === "outward";

  const toolOverride = getToolOverride(validationRequest.metadata);
  const title = getToolValidationTitle(
    validationRequest,
    canCurrentUserRespond
  );
  const alwaysAllowLabel = getToolValidationAlwaysAllowLabel(validationRequest);

  const showAlwaysAllow =
    !validationRequest.inTaskConversation &&
    (isOutward ||
      validationRequest.stake === "low" ||
      validationRequest.stake === "medium");

  const providerName = asDisplayName(validationRequest.metadata.mcpServerName);

  if (!canCurrentUserRespond) {
    return (
      <ConversationCard
        visual={icon}
        status={{
          label: `Waiting for ${triggeringUser?.fullName ?? "another person"}`,
          tone: "neutral",
        }}
        eyebrow={providerName}
        title={title}
      />
    );
  }

  return (
    <ConversationCard
      visual={icon}
      status={{ label: "Waiting for you", tone: "attention" }}
      eyebrow={providerName}
      title={title}
      meta={
        isOutward
          ? "This leaves MemoAir. Approving authorizes this exact action."
          : undefined
      }
      footerStart={
        errorMessage ? (
          <span className="text-xs font-medium text-warning-800">
            {errorMessage}
          </span>
        ) : showAlwaysAllow ? (
          <Label
            htmlFor={`never-ask-again-${validationRequest.actionId}`}
            className="flex w-fit cursor-pointer flex-row items-center gap-2 text-xs"
          >
            <Checkbox
              id={`never-ask-again-${validationRequest.actionId}`}
              checked={neverAskAgain}
              onCheckedChange={(check) => {
                setNeverAskAgain(!!check);
              }}
            />
            <span className="font-normal text-muted-foreground">
              {isOutward
                ? "I would have been fine with this happening without asking"
                : alwaysAllowLabel}
            </span>
          </Label>
        ) : null
      }
      footer={
        <>
          <Button
            label="Decline"
            variant="outline"
            size="xs"
            icon={XClose}
            disabled={isValidating}
            isPulsing={isPulsing}
            onClick={() => void handleValidation("rejected")}
          />
          <Button
            label={toolOverride?.approveLabel ?? "Allow"}
            variant="highlight"
            size="xs"
            icon={Check}
            disabled={isValidating}
            isPulsing={isPulsing}
            onClick={() => void handleValidation("approved")}
          />
        </>
      }
    >
      <ToolValidationDetails
        blockedAction={validationRequest}
        user={currentUser}
        owner={owner}
        conversationId={conversationId}
        defaultExpanded={toolOverride?.detailsExpanded}
      />
    </ConversationCard>
  );
}
